import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import PremiumDarkAtmosphere from './PremiumDarkAtmosphere';

/**
 * PricingRevealGate — Cinematic dark transition section between "C'est de ma faute"
 * (Owner Realization) and the pricing tabs.
 *
 * Purpose:
 *  - Hold the user's attention by creating a deliberate beat of tension
 *  - Bridge the dark emotional moment (truth) → the action (offers)
 *  - Make the pricing reveal feel EARNED, not just dropped on the user
 */
const DOORS = [
  { icon: '🎯', label: '1 Day', sub: 'Audit terrain · 1 journée', tone: 'from-[#06b6d4] to-[#0891b2]' },
  { icon: '🔥', label: 'Refonte', sub: '90 jours · 2 jours sur place', tone: 'from-[#F05A1A] to-[#FF7A3D]' },
  { icon: '🚀', label: 'Ouverture', sub: '3 mois · 9 jours sur place', tone: 'from-[#8b5cf6] to-[#a855f7]' },
  { icon: '🏢', label: 'Groupe', sub: '6 mois · multi-resto', tone: 'from-[#10b981] to-[#059669]' },
];

const PricingRevealGate = ({
  testid = 'pricing-reveal-gate',
  onChooseDoor,
  // --- Personalization overrides (optional, used when user comes from /resultats) ---
  customKicker,
  customTitleTop,    // e.g. "VOICI COMMENT"
  customTitleBottom, // e.g. "JE LE RÈGLE."
  customSub,         // ReactNode
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-150px' });

  const titleTop = customTitleTop || 'VOICI COMMENT';
  const titleBottom = customTitleBottom || 'JE LE RÈGLE.';
  const kickerText = customKicker || "— Maintenant que tu sais d'où ça vient —";

  const scrollToPricing = () => {
    const target = document.querySelector('[data-testid="pricing-section-top"]');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      ref={ref}
      className="relative overflow-hidden py-20 sm:py-24"
      style={{ background: 'linear-gradient(180deg, #1a0e0e 0%, #2a1414 50%, #1a0e0e 100%)' }}
      data-testid={testid}
    >
      {/* Premium atmospheric layer */}
      <PremiumDarkAtmosphere variant="default" />

      <div className="relative max-w-3xl mx-auto px-6 text-center">
        {/* Pre-headline — small, tense */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-[10px] sm:text-xs font-black uppercase tracking-[4px] text-[#F05A1A] mb-5"
        >
          {kickerText}
        </motion.p>

        {/* Massive headline */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="font-bebas text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight mb-2"
        >
          {titleTop}
        </motion.h2>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="font-bebas text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight mb-8"
          style={{
            background: 'linear-gradient(135deg, #F05A1A 0%, #FF7A3D 50%, #F05A1A 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {titleBottom}
        </motion.h2>

        {/* Subline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="text-white/65 text-base sm:text-lg leading-relaxed max-w-xl mx-auto mb-10"
        >
          {customSub || (
            <>
              4 portes. 1 méthode. <span className="text-white font-semibold">0 promesse claquée</span>.
              <br />
              Choisis celle qui correspond à ton moment.
            </>
          )}
        </motion.p>

        {/* 4 doors teaser — no prices, just identity */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-12">
          {DOORS.map((door, i) => (
            <motion.button
              key={door.label}
              type="button"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.85 + i * 0.12 }}
              whileHover={{ y: -4, scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                if (onChooseDoor) onChooseDoor(door.label.toLowerCase());
                scrollToPricing();
              }}
              className="group relative rounded-2xl p-4 sm:p-5 bg-white/[0.04] border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-[#F05A1A]/50 transition-all text-left"
              data-testid={`reveal-door-${door.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {/* Gradient accent stripe on hover */}
              <div
                className={`absolute top-0 left-0 right-0 h-0.5 rounded-t-2xl bg-gradient-to-r ${door.tone} opacity-50 group-hover:opacity-100 transition-opacity`}
                aria-hidden="true"
              />
              <div className="text-2xl sm:text-3xl mb-2">{door.icon}</div>
              <p className="font-bebas text-white text-lg sm:text-xl leading-none tracking-wide mb-1">
                {door.label}
              </p>
              <p className="text-white/55 text-[10px] sm:text-[11px] leading-snug">
                {door.sub}
              </p>
            </motion.button>
          ))}
        </div>

        {/* CTA — scroll cue */}
        <motion.button
          type="button"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 1.45 }}
          onClick={scrollToPricing}
          className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/15 hover:bg-white/10 hover:border-[#F05A1A]/40 transition-all"
          data-testid="reveal-scroll-cue"
        >
          <span className="text-white/80 text-sm font-bold uppercase tracking-widest group-hover:text-white">
            Choisis ta porte
          </span>
          <motion.span
            animate={{ y: [0, 4, 0] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
            className="text-[#F05A1A]"
          >
            <ChevronDown className="w-4 h-4" />
          </motion.span>
        </motion.button>

        {/* Tiny disclaimer line */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 0.4 } : {}}
          transition={{ duration: 0.6, delay: 1.7 }}
          className="text-white/40 text-[10px] uppercase tracking-[3px] mt-8"
        >
          Engagement long terme · Pas un coup d'éclat
        </motion.p>
      </div>
    </section>
  );
};

export default PricingRevealGate;
